// Bridge between the speaker app and the deck running in an iframe.
//
// The deck loads at /decks/<deck-id>/?session=<id>&role=speaker — same-origin
// with the speaker app, so we can call into its window.Reveal directly. No
// postMessage marshalling needed.
//
// Usage:
//   const deck = new DeckBridge();
//   deck.bind(iframeElement, reveal => { ... });   // call from slide-preview module
//   deck.onReady(reveal => { ... });               // call from any other module

export class DeckBridge {
  constructor() {
    this.iframe   = null;
    this.reveal   = null;
    this.ready    = false;
    this._waiters = [];
  }

  /** Slide-preview module calls this with the canonical iframe. */
  bind(iframe, onReady) {
    this.iframe = iframe;
    if (onReady) this._waiters.push(onReady);
    this._poll();
  }

  /** Other modules subscribe here. Fires immediately if already ready. */
  onReady(fn) {
    if (this.ready) { fn(this.reveal); return; }
    this._waiters.push(fn);
  }

  _poll() {
    const tick = () => {
      try {
        const w = this.iframe?.contentWindow;
        if (w && w.Reveal && w.Reveal.isReady && w.Reveal.isReady()) {
          this.reveal = w.Reveal;
          this.ready  = true;
          for (const fn of this._waiters) {
            try { fn(this.reveal); } catch (e) { console.error('[deck] waiter failed', e); }
          }
          this._waiters = [];
          return;
        }
      } catch (e) { /* still loading or cross-origin error */ }
      setTimeout(tick, 100);
    };
    tick();
  }

  // Navigation API (delegated to Reveal)
  next()      { this.reveal?.next(); }
  prev()      { this.reveal?.prev(); }
  right()     { this.reveal?.right(); }
  left()      { this.reveal?.left(); }
  up()        { this.reveal?.up(); }
  down()      { this.reveal?.down(); }
  toggleOverview() { this.reveal?.toggleOverview(); }
  toggleBlackout() {
    if (!this.reveal) return;
    this.reveal.isPaused() ? this.reveal.resume() : this.reveal.pause();
  }

  // State accessors
  getCurrentSlide() { return this.reveal?.getCurrentSlide() ?? null; }
  getIndices() { return this.reveal?.getIndices() ?? { h: 0, v: 0, f: -1 }; }

  /** Speaker notes for the current slide. Reveal stores them in <aside class="notes">. */
  getCurrentNotes() {
    const cur = this.getCurrentSlide();
    if (!cur) return '';
    const aside = cur.querySelector('aside.notes');
    return aside ? aside.innerHTML : '';
  }
}
